import React, {useReducer, forwardRef, useImperativeHandle} from 'react'
import {Alert, Button, H6, Modal, ModalActions} from '../../UI/index.js'
import {formSpacing, FormRow, FormStack, Input, Select, Toggle} from '../../UI/forms/index.js'
import {Reseller, ShippingRule} from '../../../../server/functions/resellers/resellers.types.js'
import {ParcelCarrier} from '../../../../server/functions/parcels/carrier.types.js'
import _ from 'lodash'
import naturalSort from '../../../utils/naturalSort.js'
import t from '../../../helpers/Languages.js'
import ParcelConditionGroupsEditor from '../../conditions/ParcelConditionGroupsEditor.js'

interface EditShipppingRuleModalProps {
    reseller: Reseller
    carriers: ParcelCarrier[]
    onChange: (shippingRule: ShippingRule) => void
}

interface EditShippingRuleModalState {
    modalIsOpen: boolean
    shippingRule?: ShippingRule
    shippingOptions: string[]
    loading: boolean
    error: string
    validation: {
        name: boolean
    }
}

export interface EditShipppingRuleModalRef {
    open: (shippingRule: ShippingRule) => void
}

const EditShipppingRuleModal = forwardRef<EditShipppingRuleModalRef, EditShipppingRuleModalProps>((props, ref) => {
    const {reseller, carriers, onChange} = props

    const initialState: EditShippingRuleModalState = {
        modalIsOpen: false,
        shippingRule: undefined,
        shippingOptions: [],
        loading: false,
        error: '',
        validation: {
            name: false
        }
    }

    const [state, setState] = useReducer((state: EditShippingRuleModalState, newState: Partial<EditShippingRuleModalState>) => ({...state, ...newState}), structuredClone(initialState))

    useImperativeHandle(ref, () => ({open}))

    const enabledCarrierNames = naturalSort(Object.keys(reseller.settings.parcels.carriers).filter((carrierName) => {
        return reseller.settings.parcels.carriers[carrierName].enabled
    })) as string[]

    const open = (shippingRule: ShippingRule) => {
        // Ensure conditionGroups is initialized
        if (!shippingRule?.conditionGroups) {
            shippingRule.conditionGroups = []
        }
        if (!shippingRule?.name) {
            setTimeout(() => {
                document.getElementById('shippingRuleName')?.focus()
            }, 10)
        }
        setState({modalIsOpen: true, shippingRule})
    }

    const close = () => {
        setState(initialState)
    }

    const save = () => {
        if (!validate()) {
            return
        }
        if (state.shippingRule) {
            onChange(state.shippingRule)
        }
        close()
    }

    const validate = () => {
        let valid = true
        let focusId = ''
        let error = ''
        let validation = {name: false}

        if (!state.shippingRule?.name) {
            valid = false
            error = t('errorsNameRequired')
            focusId = 'shippingRuleName'
            validation.name = true
        }

        setState({error, validation})

        if (!valid && focusId) {
            document.getElementById(focusId)?.focus()
        }

        return valid
    }

    const onChangeValue = (value: string | boolean, path: string) => {
        const updatedShippingRule = {...state.shippingRule!}
        _.set(updatedShippingRule, path, value)
        setState({shippingRule: updatedShippingRule})
    }

    return (
        <Modal
            style={{maxWidth: 800}}
            show={state.modalIsOpen}
            title={state.shippingRule?.id ? `Bewerk verzendregel` : 'Nieuwe verzendregel'}
            onClose={close}
            closeButton
        >
            <FormStack style={{marginBottom: formSpacing.rowGap}}>
                <Input
                    id='shippingRuleName'
                    label='Naam'
                    maxLength={50}
                    value={state.shippingRule?.name}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setState({shippingRule: {...state.shippingRule!, name: event.target.value}})
                    }}
                    isInvalid={state.validation.name}
                />

                <FormRow>
                    <Toggle
                        label='Actief'
                        checked={!!state.shippingRule?.active}
                        onChange={(event) => onChangeValue(!!event.target.checked, 'active')}
                    />
                    <div style={{flex: 1}} />
                </FormRow>
            </FormStack>

            <H6 style={{marginBottom: 12, marginTop: 36}}>{t('conditions')}</H6>

            {state.shippingRule &&
                <ParcelConditionGroupsEditor
                    conditionGroups={state.shippingRule.conditionGroups}
                    matchAll={state.shippingRule.matchAllConditions}
                    reseller={reseller}
                    onChange={(conditionGroups, matchAllConditions) => setState({shippingRule: {...state.shippingRule!, conditionGroups, matchAllConditions}})}
                />
            }

            <H6 style={{marginBottom: 12, marginTop: 48}}>{t('carrier')}</H6>

            <FormRow style={{marginTop: formSpacing.section, marginBottom: formSpacing.rowGap}}>
                <Select
                    style={{flex: 1}}
                    options={enabledCarrierNames.map((carrier) => ({title: carrier, value: carrier, img: carriers.find((c) => c.name === carrier)?.logo}))}
                    value={state.shippingRule?.carrier}
                    onChange={(event) => onChangeValue(event.target.value, 'carrier')}
                />
            </FormRow>

            <Alert
                style={{marginBottom: 6, marginTop: 24}}
                variant='info'
            >
                De verzendregel wordt genegeerd bij ongeldige combinaties of wanneer de gekozen vervoerder niet kan voldoen aan de voorwaarden.
            </Alert>

            {state.error && <Alert style={{marginBottom: 6}} variant='danger'>{state.error}</Alert>}

            <ModalActions style={{marginTop: 12}}>
                <Button
                    style={{marginTop: 24}}
                    onClick={save}
                    loading={state.loading}
                    label={t('save')}
                />
            </ModalActions>
        </Modal>
    )
})

EditShipppingRuleModal.displayName = 'EditShipppingRuleModal'

export default EditShipppingRuleModal
